import Loader from "components/shared/loader";
import { dateTimeInUtcOffset, dateTimeToView, formatUtcOffset } from "helpers/date";
import React, { useEffect, useState } from "react";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

const dateInOrganizationTimezone = (value, organizationTimezone) => {
    const organizationUtcOffset = formatUtcOffset(organizationTimezone);
    return organizationUtcOffset ? dateTimeInUtcOffset(value, organizationUtcOffset) : value;
};

const formatDateValue = (value, organizationTimezone) => {
    if (!value || typeof value !== "string" || !ISO_DATE_RE.test(value)) {
        return value;
    }

    return dateTimeToView(dateInOrganizationTimezone(value, organizationTimezone), "DD.MM.YYYY HH:mm");
};

const sanitizeText = (value) => {
    if (value === null || value === undefined) {
        return value;
    }

    return String(value)
        .split("")
        .filter((char) => {
            const code = char.charCodeAt(0);
            return !((code >= 0 && code <= 31) || (code >= 127 && code <= 159));
        })
        .join("");
};

const tryParseJson = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed || !["{", "["].includes(trimmed[0])) {
        return null;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
};

const prettifyKey = (key) => {
    const labels = {
        id: "ID",
        tripSheetId: "Путевой лист",
        bidId: "Заявка",
        arrivalTime: "Прибытие",
        departureTime: "Убытие",
        counterpartyDetails: "Заказчик",
        loadingAddress: "Погрузка",
        unloadingAddress: "Разгрузка",
        cargoDetails: "Груз",
        tripCount: "Кол-во ездок",
        distance: "Расстояние, км",
        weight: "Вес, т",
    };

    return labels[key] || key;
};

const normalizeValue = (value, organizationTimezone) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    return sanitizeText(formatDateValue(value, organizationTimezone));
};

const renderPrimitive = (value, organizationTimezone) => {
    const cleanValue = normalizeValue(value, organizationTimezone);

    if (cleanValue === null) {
        return <span className="text-muted">Не указано</span>;
    }

    return <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{cleanValue}</span>;
};

const getObjectEntries = (value) => {
    const parsedValue = tryParseJson(value);

    if (!parsedValue || Array.isArray(parsedValue)) {
        return null;
    }

    return parsedValue;
};

const CompareModalBody = ({ title, previousValue, newValue, organizationTimezone }) => {
    const previousObject = getObjectEntries(previousValue);
    const newObject = getObjectEntries(newValue);

    if (!previousObject && !newObject) {
        return (
            <div>
                <div className="font-weight-bold mb-2">{title}</div>
                <table className="table table-sm mb-0">
                    <thead className="thead-light">
                        <tr>
                            <th>Было</th>
                            <th>Стало</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{renderPrimitive(previousValue, organizationTimezone)}</td>
                            <td>{renderPrimitive(newValue, organizationTimezone)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    const keys = Array.from(new Set([...Object.keys(previousObject || {}), ...Object.keys(newObject || {})]));

    return (
        <div>
            <div className="font-weight-bold mb-2">{title}</div>
            <table className="table table-sm mb-0">
                <thead className="thead-light">
                    <tr>
                        <th>Поле</th>
                        <th>Было</th>
                        <th>Стало</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map((key) => {
                        const previousItem = normalizeValue(previousObject?.[key], organizationTimezone);
                        const newItem = normalizeValue(newObject?.[key], organizationTimezone);
                        const changed = previousItem !== newItem;

                        return (
                            <tr key={key}>
                                <td className={changed ? "font-weight-bold" : ""}>{prettifyKey(key)}</td>
                                <td>{renderPrimitive(previousObject?.[key], organizationTimezone)}</td>
                                <td>{renderPrimitive(newObject?.[key], organizationTimezone)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const getPreviewText = (value, organizationTimezone) => {
    const parsedValue = tryParseJson(value);

    if (!parsedValue) {
        const cleanValue = normalizeValue(value, organizationTimezone);
        if (cleanValue === null) {
            return "Не указано";
        }

        return String(cleanValue).slice(0, 120);
    }

    if (Array.isArray(parsedValue)) {
        return `Массив: ${parsedValue.length}`;
    }

    const previewParts = Object.entries(parsedValue)
        .slice(0, 3)
        .map(
            ([key, itemValue]) =>
                `${prettifyKey(key)}: ${sanitizeText(formatDateValue(itemValue, organizationTimezone)) ?? "Не указано"}`,
        );

    return previewParts.join(", ");
};

const ValueCell = ({ title, value, previousValue, newValue, modal, organizationTimezone }) => {
    const parsedValue = tryParseJson(value);

    if (!parsedValue || !modal) {
        return renderPrimitive(value, organizationTimezone);
    }

    return (
        <div>
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {getPreviewText(value, organizationTimezone)}
            </div>
            <div
                className="btn btn-link btn-sm px-0 mt-1 align-baseline"
                onClick={() =>
                    modal({
                        name: "default",
                        title,
                        windowMod: "lg",
                        CustomBody: CompareModalBody,
                        bodyData: { title, previousValue, newValue, organizationTimezone },
                    })
                }
            >
                Показать полностью
            </div>
        </div>
    );
};

const ChangeLog = ({ tripSheetId, source, modal, organizationTimezone }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!tripSheetId) {
            return;
        }

        setIsLoading(true);
        source
            .getChanges(tripSheetId)
            .then((data) => {
                setItems(data || []);
            })
            .catch(() => {
                setItems([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [tripSheetId, source]);

    if (isLoading) {
        return <Loader prefix="absolute-center" />;
    }

    if (items.length === 0) {
        return (
            <div className="overflow-auto h-100 native-scroll__wrap">
                <div className="native-scroll__container py-3 text-center text-muted">Изменений нет</div>
            </div>
        );
    }

    return (
        <div className="overflow-auto h-100 native-scroll__wrap">
            <div className="native-scroll__container">
                <table className="table table-sm">
                    <thead className="thead-light">
                        <tr>
                            <th>Дата изменения</th>
                            <th>Кто изменил</th>
                            <th>Поле</th>
                            <th>Старое значение</th>
                            <th>Новое значение</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((entry) =>
                            entry.changes.map((change, index) => (
                                <tr key={`${entry.id}-${index}`}>
                                    {index === 0 && (
                                        <>
                                            <td rowSpan={entry.changes.length}>
                                                {dateTimeToView(
                                                    dateInOrganizationTimezone(entry.changedAt, organizationTimezone),
                                                    "DD.MM.YYYY HH:mm",
                                                )}
                                            </td>
                                            <td rowSpan={entry.changes.length}>
                                                {sanitizeText(entry.changedBy?.displayName) ||
                                                    "Неизвестный пользователь"}
                                            </td>
                                        </>
                                    )}
                                    <td>{sanitizeText(change.displayName)}</td>
                                    <td>
                                        <ValueCell
                                            title={`${sanitizeText(change.displayName)}: изменение`}
                                            value={change.previousValue}
                                            previousValue={change.previousValue}
                                            newValue={change.newValue}
                                            modal={modal}
                                            organizationTimezone={organizationTimezone}
                                        />
                                    </td>
                                    <td>
                                        <ValueCell
                                            title={`${sanitizeText(change.displayName)}: изменение`}
                                            value={change.newValue}
                                            previousValue={change.previousValue}
                                            newValue={change.newValue}
                                            modal={modal}
                                            organizationTimezone={organizationTimezone}
                                        />
                                    </td>
                                </tr>
                            )),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ChangeLog;
